import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const BASE = process.env.REACT_APP_BACKEND_URL;

export class RpcError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
    this.data = { httpStatus: status };
  }
}

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg ?? JSON.stringify(e)).join(" ");
  return detail.msg ?? String(detail);
}

export async function call(path, input) {
  let res;
  try {
    res = await fetch(`${BASE}/api/rpc/${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ input: input ?? null }),
    });
  } catch {
    throw new RpcError("Couldn't reach the server. Check your connection and try again.", 0);
  }
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) throw new RpcError(formatDetail(body?.detail), res.status);
  return body?.data ?? null;
}

const keyOf = (path) => path.split(".");

function procedure(path) {
  return {
    useQuery: (input, opts = {}) =>
      useQuery({ queryKey: [...keyOf(path), input ?? null], queryFn: () => call(path, input), ...opts }),
    useMutation: (opts = {}) => useMutation({ mutationFn: (input) => call(path, input), ...opts }),
  };
}

function useUtils() {
  const qc = useQueryClient();
  return new Proxy(
    {},
    {
      get: (_, router) =>
        new Proxy(
          {},
          {
            get: (__, name) => ({
              invalidate: () => qc.invalidateQueries({ queryKey: [router, name] }),
              refetch: () => qc.refetchQueries({ queryKey: [router, name] }),
            }),
          },
        ),
    },
  );
}

export const trpc = new Proxy(
  {},
  {
    get: (_, router) =>
      router === "useUtils"
        ? useUtils
        : new Proxy({}, { get: (__, name) => procedure(`${String(router)}.${String(name)}`) }),
  },
);
